import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity, ScrollView,
  ActivityIndicator, RefreshControl, Modal, Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import apiClient from '../utils/axiosConfig';

const STATUS_CONFIG = {
  pending:     { color: '#d97706', bg: '#fef3c7', label: 'Sin piloto',   icon: 'time-outline' },
  assigned:    { color: '#7c3aed', bg: '#ede9fe', label: 'Asignada',     icon: 'person-outline' },
  in_progress: { color: '#1d4ed8', bg: '#dbeafe', label: 'En ejecución', icon: 'airplane-outline' },
  completed:   { color: '#16a34a', bg: '#dcfce7', label: 'Completada',   icon: 'checkmark-circle-outline' },
  cancelled:   { color: '#9ca3af', bg: '#f3f4f6', label: 'Cancelada',    icon: 'close-circle-outline' },
};

const FILTERS = [
  { key: 'all',         label: 'Todas' },
  { key: 'no_pilot',    label: 'Sin piloto' },
  { key: 'assigned',    label: 'Asignadas' },
  { key: 'in_progress', label: 'En ejecución' },
  { key: 'completed',   label: 'Completadas' },
];

const PILOT_COLORS = ['#1d4ed8', '#059669', '#7c3aed', '#dc2626', '#d97706', '#0891b2'];
const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const missionDate = (req) => req.confirmed_date || req.application_date || req.scheduled_date || null;

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatShort = (dateStr) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const weekLabel = (weekStart) => {
  const weekEnd = addDays(weekStart, 6);
  const sDay = weekStart.getDate();
  const sMon = weekStart.toLocaleDateString('es-CL', { month: 'short' });
  const eDay = weekEnd.getDate();
  const eMon = weekEnd.toLocaleDateString('es-CL', { month: 'short' });
  const year = weekEnd.getFullYear();
  if (sMon === eMon) return `${sDay}–${eDay} ${sMon} ${year}`;
  return `${sDay} ${sMon} – ${eDay} ${eMon} ${year}`;
};

const groupByDate = (missions) => {
  const groups = {};
  missions.forEach(r => {
    const key = missionDate(r) || '__sin_fecha__';
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  const keys = Object.keys(groups).sort((a, b) => {
    if (a === '__sin_fecha__') return 1;
    if (b === '__sin_fecha__') return -1;
    return a.localeCompare(b);
  });
  return keys.map(k => ({ date: k, missions: groups[k] }));
};

// ── Calendario inline ────────────────────────────────────────────────────────
function MiniCalendar({ selected, onSelect, highlightDate }) {
  const [month, setMonth] = useState(() => {
    const base = selected ? new Date(selected + 'T12:00:00') : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const year  = month.getFullYear();
  const mon   = month.getMonth();
  const first = new Date(year, mon, 1);
  // Lunes = 0 en nuestra grilla (getDay: 0=Dom,1=Lun...)
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const cells = Array(startOffset).fill(null).concat(
    Array.from({ length: daysInMonth }, (_, i) => i + 1)
  );
  // Rellenar hasta múltiplo de 7
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr    = toDateStr(new Date());
  const hlStr       = highlightDate;

  return (
    <View style={cal.wrapper}>
      {/* Nav */}
      <View style={cal.nav}>
        <TouchableOpacity onPress={() => setMonth(new Date(year, mon - 1, 1))} style={cal.navBtn}>
          <Ionicons name="chevron-back-outline" size={18} color="#374151" />
        </TouchableOpacity>
        <Text style={cal.monthLabel}>{MONTHS[mon]} {year}</Text>
        <TouchableOpacity onPress={() => setMonth(new Date(year, mon + 1, 1))} style={cal.navBtn}>
          <Ionicons name="chevron-forward-outline" size={18} color="#374151" />
        </TouchableOpacity>
      </View>
      {/* Días semana */}
      <View style={cal.row}>
        {DAYS.map(d => <Text key={d} style={cal.dayHeader}>{d}</Text>)}
      </View>
      {/* Celdas */}
      {Array.from({ length: cells.length / 7 }, (_, wi) => (
        <View key={wi} style={cal.row}>
          {cells.slice(wi * 7, wi * 7 + 7).map((day, di) => {
            if (!day) return <View key={di} style={cal.cell} />;
            const dateStr = toDateStr(new Date(year, mon, day));
            const isSelected  = dateStr === selected;
            const isHighlight = dateStr === hlStr;
            const isToday     = dateStr === todayStr;
            return (
              <TouchableOpacity
                key={di}
                style={[cal.cell, isSelected && cal.cellSelected, isHighlight && !isSelected && cal.cellHighlight]}
                onPress={() => onSelect(dateStr)}
              >
                <Text style={[
                  cal.dayText,
                  isSelected && cal.dayTextSelected,
                  isHighlight && !isSelected && cal.dayTextHighlight,
                  isToday && !isSelected && { fontWeight: '700', color: '#1d4ed8' },
                ]}>
                  {day}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
    </View>
  );
}

// ────────────────────────────────────────────────────────────────────────────

export default function OperatorMissionsScreen({ navigation }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter]         = useState('all');
  const [pilotFilter, setPilotFilter] = useState(null);
  const [view, setView]             = useState('list');
  const [assigning, setAssigning]   = useState(null);
  const [pickerReq, setPickerReq]   = useState(null);
  const [pilotPickerOpen, setPilotPickerOpen] = useState(false);
  const [dateReq, setDateReq]       = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [fleet, setFleet]           = useState([]);        // aeronaves del operador
  const [selectedPilotId, setSelectedPilotId] = useState(null);  // piloto elegido en picker
  const [selectedAircraftId, setSelectedAircraftId] = useState(null); // aeronave elegida en picker
  const [weekStart, setWeekStart]   = useState(() => {
    const today = new Date();
    const day   = today.getDay();
    const diff  = (day === 0 ? -6 : 1 - day);
    const mon   = new Date(today);
    mon.setDate(today.getDate() + diff);
    mon.setHours(0,0,0,0);
    return mon;
  });

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [missionsResp, fleetResp] = await Promise.all([
        apiClient.get('/operator-missions/'),
        apiClient.get('/operator-fleet/'),
      ]);
      setData(missionsResp.data);
      setFleet(fleetResp.data || []);
    } catch (e) {
      if (typeof window !== 'undefined') window.alert(e?.response?.data?.error || 'Error al cargar');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  // Aeronaves del piloto seleccionado en el picker
  const aircraftForPilot = (pilotId) => {
    if (!pilotId) return fleet;
    return fleet.filter(a => (a.pilots || []).some(p => p.id === pilotId));
  };

  const assignPilot = async (requestId, pilotId, aircraftId) => {
    setPickerReq(null);
    setSelectedPilotId(null);
    setSelectedAircraftId(null);
    setAssigning(requestId);
    try {
      await apiClient.post(`/operator-missions/${requestId}/assign-pilot/`, {
        pilot_id: pilotId || null,
        aircraft_id: aircraftId || null,
      });
      await load(true);
    } catch (e) {
      if (typeof window !== 'undefined') window.alert(e?.response?.data?.error || 'Error al asignar');
    } finally { setAssigning(null); }
  };

  const confirmDate = async () => {
    const req = dateReq;
    setDateReq(null);
    try {
      await apiClient.post(`/mission-requests/${req.id}/confirm-date/`, { confirmed_date: selectedDate || null });
      await load(true);
    } catch (e) {
      if (typeof window !== 'undefined') window.alert(e?.response?.data?.error || 'Error al guardar fecha');
    }
  };

  const openDateModal = (req) => {
    setSelectedDate(req.confirmed_date || req.application_date || req.scheduled_date || null);
    setDateReq(req);
  };

  const exportPdf = async () => {
    try {
      const { getToken } = await import('../services/auth/authService');
      const { API_URL } = await import('../utils/config');
      const token = await getToken();
      const baseUrl = API_URL.replace('/api/v1', '');
      const weekStr = toDateStr(weekStart);
      const url = `${baseUrl}/api/v1/operator-missions/pdf/?week=${weekStr}&token=${token}`;
      const { Linking } = await import('react-native');
      await Linking.openURL(url);
    } catch (e) {
      if (typeof window !== 'undefined') window.alert('Error al generar PDF');
    }
  };

  // Filtrado por semana para la vista agenda
  const filtered = (data?.results || []).filter(r => {
    const statusOk = filter === 'all' ||
      (filter === 'no_pilot'    && !r.pilot) ||
      (filter === 'assigned'    && r.status === 'assigned') ||
      (filter === 'in_progress' && r.status === 'in_progress') ||
      (filter === 'completed'   && r.status === 'completed');
    const pilotOk = !pilotFilter || r.pilot?.id === pilotFilter;
    return statusOk && pilotOk;
  });

  const pilotColorMap = {};
  (data?.pilots || []).forEach((p, i) => { pilotColorMap[p.id] = PILOT_COLORS[i % PILOT_COLORS.length]; });

  const weekEnd      = addDays(weekStart, 6);
  const weekStartStr = toDateStr(weekStart);
  const weekEndStr   = toDateStr(weekEnd);
  const filteredByWeek = filtered.filter(r => {
    const d = missionDate(r);
    if (!d) return false; // sin fecha: no mostrar en la agenda, van al banner
    return d >= weekStartStr && d <= weekEndStr;
  });

  if (loading) return <View style={styles.centered}><ActivityIndicator size="large" color="#2c3e50" /></View>;

  // ── Card ─────────────────────────────────────────────────────────────────
  const MissionCard = ({ req, showDate = false }) => {
    const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
    const pilotColor = req.pilot ? (pilotColorMap[req.pilot.id] || '#6b7280') : null;
    const canAssign  = req.status !== 'completed' && req.status !== 'cancelled';
    const reqDate    = req.application_date || req.scheduled_date;

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('PilotMissionDetail', {
          item: { ...req, _type: 'mission_request', _raw_id: req.id, _mission_id: req.mission_id, mr_type: req.type },
          readOnly: true,
        })}
        activeOpacity={0.8}
      >
        <View style={styles.cardTop}>
          <View style={[styles.typeIcon, { backgroundColor: req.type === 'multispectral' ? '#fef3c7' : '#dbeafe' }]}>
            <Ionicons name={req.type === 'multispectral' ? 'leaf-outline' : 'water-outline'} size={18}
              color={req.type === 'multispectral' ? '#d97706' : '#1d4ed8'} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.cardTitle} numberOfLines={1}>{req.location_name || req.enterprise_name || 'Sin campo'}</Text>
            <Text style={styles.cardId}>
              {req.type === 'multispectral'
                ? `Misión #${req.mission_id}`
                : `Orden #${String(req.order_folio || '').padStart(4, '0')} · Misión #${req.mission_id}`}
            </Text>
            {req.type === 'application' && (req.field_blocks || []).length > 0 && (
              <Text style={styles.cardSub} numberOfLines={1}>{req.field_blocks.map(b => b.name).join(', ')}</Text>
            )}
            {req.type === 'multispectral' && (req.objectives_list || []).length > 0 && (
              <Text style={styles.cardSub} numberOfLines={1}>{req.objectives_list.join(' · ')}</Text>
            )}
            {/* Fechas */}
            {(reqDate || canAssign) && (
              <View style={styles.datesRow}>
                {reqDate ? (
                  <>
                    <View style={styles.dateItem}>
                      <Text style={styles.dateLabelText}>Solicitado</Text>
                      <Text style={styles.dateValue}>{formatShort(reqDate)}</Text>
                    </View>
                    <Ionicons name="arrow-forward-outline" size={12} color="#d1d5db" />
                  </>
                ) : null}
                {req.confirmed_date ? (
                  <TouchableOpacity style={styles.dateProposed} onPress={() => openDateModal(req)}>
                    {reqDate && <Text style={styles.dateLabelText}>Programado</Text>}
                    <View style={styles.dateProposedValue}>
                      <Text style={styles.dateProposedText}>{formatShort(req.confirmed_date)}</Text>
                      <Ionicons name="pencil-outline" size={10} color="#1d4ed8" />
                    </View>
                  </TouchableOpacity>
                ) : canAssign ? (
                  <TouchableOpacity style={styles.dateProposedEmpty} onPress={() => openDateModal(req)}>
                    <Ionicons name="calendar-outline" size={12} color="#1d4ed8" />
                    <Text style={styles.dateProposedEmptyText}>
                      {reqDate ? 'Cambiar fecha' : 'Asignar fecha'}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            )}
          </View>
          <View style={[styles.statusBadge, { backgroundColor: cfg.bg }]}>
            <Ionicons name={cfg.icon} size={12} color={cfg.color} style={{ marginRight: 4 }} />
            <Text style={[styles.statusText, { color: cfg.color }]}>{cfg.label}</Text>
          </View>
        </View>

        <View style={styles.cardFooter}>
          {req.pilot?.name ? (
            <View style={styles.pilotRow}>
              <View style={[styles.pilotDot, { backgroundColor: pilotColor }]} />
              <View>
                <Text style={styles.pilotName}>{req.pilot.name}</Text>
                {req.aircraft?.name && (
                  <Text style={styles.aircraftName}>
                    <Ionicons name="airplane-outline" size={11} color="#9ca3af" /> {req.aircraft.name}
                  </Text>
                )}
              </View>
            </View>
          ) : (
            <Text style={styles.noPilot}>Sin piloto asignado</Text>
          )}
          {canAssign && (
            <TouchableOpacity style={styles.assignBtn} onPress={() => setPickerReq(req)} disabled={assigning === req.id}>
              {assigning === req.id
                ? <ActivityIndicator size="small" color="#2c3e50" />
                : <><Ionicons name="person-add-outline" size={13} color="#2c3e50" />
                    <Text style={styles.assignBtnText}>{req.pilot?.name ? 'Reasignar' : 'Asignar'}</Text></>
              }
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ── Vista Agenda ──────────────────────────────────────────────────────────
  const AgendaView = () => {
    const groups = groupByDate(filteredByWeek);
    const assignedPilots = [...new Set(filteredByWeek.filter(r => r.pilot).map(r => r.pilot.id))]
      .map(id => (data?.pilots || []).find(p => p.id === id)).filter(Boolean);

    return (
      <ScrollView contentContainerStyle={{ padding: 12, paddingBottom: 32 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} tintColor="#2c3e50" />}>

        {/* Navegación de semana */}
        <View style={styles.weekNav}>
          <TouchableOpacity style={styles.weekNavBtn} onPress={() => setWeekStart(addDays(weekStart, -7))}>
            <Ionicons name="chevron-back-outline" size={18} color="#374151" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.weekNavCenter} onPress={() => {
            const today = new Date();
            const day = today.getDay();
            const diff = (day === 0 ? -6 : 1 - day);
            const mon = new Date(today);
            mon.setDate(today.getDate() + diff);
            mon.setHours(0,0,0,0);
            setWeekStart(mon);
          }}>
            <Text style={styles.weekNavLabel}>{weekLabel(weekStart)}</Text>
            <Text style={styles.weekNavHint}>Toca para ir a esta semana</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.weekNavBtn} onPress={() => setWeekStart(addDays(weekStart, 7))}>
            <Ionicons name="chevron-forward-outline" size={18} color="#374151" />
          </TouchableOpacity>
        </View>

        {/* Exportar PDF */}
        <TouchableOpacity style={styles.exportBtn} onPress={exportPdf}>
          <Ionicons name="document-text-outline" size={14} color="#2c3e50" />
          <Text style={styles.exportBtnText}>Exportar semana como PDF</Text>
        </TouchableOpacity>

        {/* Banner misiones sin fecha */}
        {(() => {
          const sinFecha = filtered.filter(r => !missionDate(r));
          if (sinFecha.length === 0) return null;
          return (
            <TouchableOpacity
              style={styles.noDateBanner}
              onPress={() => { setFilter('no_pilot'); setView('list'); }}
            >
              <Ionicons name="warning-outline" size={15} color="#d97706" />
              <Text style={styles.noDateBannerText}>
                {sinFecha.length} {sinFecha.length === 1 ? 'misión sin' : 'misiones sin'} fecha programada
              </Text>
              <Ionicons name="chevron-forward-outline" size={14} color="#d97706" />
            </TouchableOpacity>
          );
        })()}

        {assignedPilots.length > 0 && (
          <View style={styles.legend}>
            {assignedPilots.map(p => (
              <View key={p.id} style={styles.legendItem}>
                <View style={[styles.pilotDot, { backgroundColor: pilotColorMap[p.id] }]} />
                <Text style={styles.legendText}>{p.name}</Text>
              </View>
            ))}
          </View>
        )}

        {groups.length === 0 ? (
          <View style={styles.empty}>
            <Ionicons name="calendar-outline" size={48} color="#e5e7eb" />
            <Text style={styles.emptyTitle}>Sin misiones esta semana</Text>
            <Text style={styles.emptyText}>Navega a otra semana o revisa los filtros.</Text>
          </View>
        ) : (
          groups.map(({ date, missions }) => (
            <View key={date}>
              <View style={styles.dateSeparator}>
                <View style={styles.dateLine} />
                <Text style={styles.dateLabel}>{date === '__sin_fecha__' ? 'Sin fecha programada' : formatDate(date)}</Text>
                <View style={styles.dateLine} />
              </View>
              {missions.map(req => <MissionCard key={req.id} req={req} />)}
            </View>
          ))
        )}
      </ScrollView>
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f7fa' }}>
      <View style={styles.header}>
        <View>
          <Text style={styles.headerTitle}>{data?.operator?.name || 'Operaciones'}</Text>
          <Text style={styles.headerSub}>{filtered.length} misiones</Text>
        </View>
        <View style={styles.viewToggle}>
          <TouchableOpacity style={[styles.viewBtn, view === 'list' && styles.viewBtnActive]} onPress={() => setView('list')}>
            <Ionicons name="list-outline" size={16} color={view === 'list' ? '#fff' : '#6b7280'} />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.viewBtn, view === 'agenda' && styles.viewBtnActive]} onPress={() => setView('agenda')}>
            <Ionicons name="calendar-outline" size={16} color={view === 'agenda' ? '#fff' : '#6b7280'} />
          </TouchableOpacity>
        </View>
      </View>


      <View style={styles.filterRow}>
        {FILTERS.map(f => (
          <TouchableOpacity key={f.key} style={[styles.filterBtn, filter === f.key && styles.filterBtnActive]} onPress={() => setFilter(f.key)}>
            <Text style={[styles.filterText, filter === f.key && styles.filterTextActive]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Dropdown piloto */}
      {(data?.pilots || []).length > 0 && (
        <TouchableOpacity style={styles.pilotDropdown} onPress={() => setPilotPickerOpen(true)}>
          <Ionicons name="person-outline" size={14} color="#6b7280" />
          <Text style={styles.pilotDropdownText}>
            {pilotFilter ? (data.pilots.find(p => p.id === pilotFilter)?.name || 'Piloto') : 'Todos los pilotos'}
          </Text>
          <Ionicons name="chevron-down-outline" size={14} color="#6b7280" />
        </TouchableOpacity>
      )}

      {view === 'agenda' ? <AgendaView /> : (
        <FlatList data={filtered} keyExtractor={item => `op-${item.id}`}
          renderItem={({ item: req }) => <MissionCard req={req} showDate />}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} tintColor="#2c3e50" />}
          contentContainerStyle={{ padding: 12, paddingBottom: 32 }}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Ionicons name="airplane-outline" size={48} color="#e5e7eb" />
              <Text style={styles.emptyTitle}>Sin misiones</Text>
              <Text style={styles.emptyText}>{filter === 'all' ? 'No hay misiones asignadas aún.' : 'No hay misiones con este filtro.'}</Text>
            </View>
          }
        />
      )}

      {/* ── Modal fecha con calendario ──────────────────────────────────── */}
      <Modal visible={!!dateReq} transparent animationType="slide" onRequestClose={() => setDateReq(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setDateReq(null)}>
          <Pressable style={[styles.modalBox, { paddingBottom: 24 }]} onPress={e => e.stopPropagation?.()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Cambiar fecha de vuelo</Text>
              <Text style={styles.modalSub} numberOfLines={1}>{dateReq?.location_name || dateReq?.enterprise_name || ''}</Text>
            </View>

            {/* Referencia: fecha solicitada */}
            {(dateReq?.application_date || dateReq?.scheduled_date) && (
              <View style={styles.calHint}>
                <Ionicons name="information-circle-outline" size={14} color="#9ca3af" />
                <Text style={styles.calHintText}>
                  Solicitado por el agricultor: {formatShort(dateReq.application_date || dateReq.scheduled_date)}
                </Text>
              </View>
            )}

            {/* Calendário */}
            <MiniCalendar
              selected={selectedDate}
              onSelect={setSelectedDate}
              highlightDate={dateReq?.application_date || dateReq?.scheduled_date}
            />

            {/* Fecha seleccionada */}
            <View style={styles.calSelected}>
              {selectedDate ? (
                <Text style={styles.calSelectedText}>
                  Fecha seleccionada: <Text style={{ fontWeight: '700', color: '#1d4ed8' }}>
                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </Text>
                </Text>
              ) : (
                <Text style={styles.calSelectedText}>Selecciona un día en el calendario</Text>
              )}
            </View>

            {/* Botones */}
            <View style={styles.calBtns}>
              {dateReq?.confirmed_date && (
                <TouchableOpacity style={styles.calBtnSecondary} onPress={() => { setSelectedDate(null); confirmDate(); }}>
                  <Text style={styles.calBtnSecondaryText}>Quitar fecha</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.calBtnPrimary, !selectedDate && { opacity: 0.4 }]}
                onPress={confirmDate}
                disabled={!selectedDate}
              >
                <Text style={styles.calBtnPrimaryText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Modal filtro piloto ─────────────────────────────────────────── */}
      <Modal visible={pilotPickerOpen} transparent animationType="fade" onRequestClose={() => setPilotPickerOpen(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setPilotPickerOpen(false)}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Filtrar por piloto</Text>
            </View>
            <TouchableOpacity
              style={[styles.pilotOption, styles.pilotOptionFirst, !pilotFilter && styles.pilotOptionActive]}
              onPress={() => { setPilotFilter(null); setPilotPickerOpen(false); }}
            >
              <View style={styles.pilotOptionIcon}>
                <Ionicons name="people-outline" size={16} color="#6b7280" />
              </View>
              <Text style={[styles.pilotOptionText, !pilotFilter && { fontWeight: '700' }]}>Todos los pilotos</Text>
              {!pilotFilter && <Ionicons name="checkmark" size={16} color="#16a34a" />}
            </TouchableOpacity>
            {(data?.pilots || []).map(pilot => {
              const isActive = pilotFilter === pilot.id;
              return (
                <TouchableOpacity
                  key={pilot.id}
                  style={[styles.pilotOption, isActive && styles.pilotOptionActive]}
                  onPress={() => { setPilotFilter(pilot.id); setPilotPickerOpen(false); }}
                >
                  <View style={[styles.pilotOptionIcon, { backgroundColor: pilotColorMap[pilot.id] + '22' }]}>
                    <Ionicons name="person-outline" size={16} color={pilotColorMap[pilot.id]} />
                  </View>
                  <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View style={[styles.pilotDot, { backgroundColor: pilotColorMap[pilot.id] }]} />
                    <Text style={[styles.pilotOptionText, isActive && { fontWeight: '700', color: '#16a34a' }]}>{pilot.name}</Text>
                  </View>
                  {isActive && <Ionicons name="checkmark" size={16} color="#16a34a" />}
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity style={styles.modalCancel} onPress={() => setPilotPickerOpen(false)}>
              <Text style={styles.modalCancelText}>Cerrar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Modal pilotos ─────────────────────────────────────────────────── */}
      <Modal visible={!!pickerReq} transparent animationType="fade" onRequestClose={() => setPickerReq(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setPickerReq(null)}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Asignar piloto</Text>
              <Text style={styles.modalSub} numberOfLines={1}>{pickerReq?.location_name || pickerReq?.enterprise_name || ''}</Text>
            </View>
            <TouchableOpacity style={[styles.pilotOption, styles.pilotOptionFirst]} onPress={() => assignPilot(pickerReq.id, null, null)}>
              <View style={styles.pilotOptionIcon}><Ionicons name="person-remove-outline" size={16} color="#9ca3af" /></View>
              <Text style={styles.pilotOptionText}>Sin asignar</Text>
            </TouchableOpacity>
            {(data?.pilots || []).map(pilot => {
              const isCurrent = pickerReq?.pilot?.id === pilot.id;
              const pilotAircraft = aircraftForPilot(pilot.id);
              const isSelected = selectedPilotId === pilot.id;
              return (
                <View key={pilot.id}>
                  <TouchableOpacity
                    style={[styles.pilotOption, (isCurrent || isSelected) && styles.pilotOptionActive]}
                    onPress={() => {
                      if (isSelected) {
                        setSelectedPilotId(null);
                        setSelectedAircraftId(null);
                      } else {
                        setSelectedPilotId(pilot.id);
                        setSelectedAircraftId(null);
                      }
                    }}
                  >
                    <View style={[styles.pilotOptionIcon, { backgroundColor: pilotColorMap[pilot.id] + '22' }]}>
                      <Ionicons name={isCurrent ? 'checkmark-circle' : 'person-outline'} size={16}
                        color={isCurrent ? '#16a34a' : pilotColorMap[pilot.id]} />
                    </View>
                    <View style={styles.pilotOptionDot}>
                      <View style={[styles.pilotDot, { backgroundColor: pilotColorMap[pilot.id] }]} />
                      <Text style={[styles.pilotOptionText, isCurrent && { color: '#16a34a', fontWeight: '700' }]}>{pilot.name}</Text>
                    </View>
                    {isCurrent && !isSelected && <Text style={styles.pilotOptionCurrent}>Asignado</Text>}
                    {isSelected && <Ionicons name="chevron-down" size={14} color="#6b7280" />}
                  </TouchableOpacity>
                  {/* Selector de aeronave — solo visible cuando este piloto está seleccionado */}
                  {isSelected && (
                    <View style={styles.aircraftPicker}>
                      {pilotAircraft.length === 0 && (
                        <Text style={styles.noAircraftText}>Sin aeronaves asignadas a este piloto</Text>
                      )}
                      {pilotAircraft.map(ac => {
                        const isAcSelected = selectedAircraftId === ac.id;
                        return (
                          <TouchableOpacity
                            key={ac.id}
                            style={[styles.aircraftOption, isAcSelected && styles.aircraftOptionActive]}
                            onPress={() => setSelectedAircraftId(isAcSelected ? null : ac.id)}
                          >
                            <Ionicons name="airplane-outline" size={15} color={isAcSelected ? '#1d4ed8' : '#6b7280'} />
                            <Text style={[styles.aircraftOptionText, isAcSelected && { color: '#1d4ed8', fontWeight: '700' }]}>
                              {ac.display}
                            </Text>
                            {isAcSelected && <Ionicons name="checkmark" size={14} color="#1d4ed8" />}
                          </TouchableOpacity>
                        );
                      })}
                      <TouchableOpacity
                        style={[styles.confirmAssignBtn, !selectedAircraftId && pilotAircraft.length > 0 && styles.confirmAssignBtnGray]}
                        onPress={() => assignPilot(pickerReq.id, pilot.id, selectedAircraftId)}
                      >
                        <Text style={styles.confirmAssignBtnText}>
                          {selectedAircraftId ? 'Confirmar asignación' : 'Asignar sin aeronave'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}
            <TouchableOpacity style={styles.modalCancel} onPress={() => { setPickerReq(null); setSelectedPilotId(null); setSelectedAircraftId(null); }}>
              <Text style={styles.modalCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const cal = StyleSheet.create({
  wrapper:         { paddingHorizontal: 16, paddingTop: 4 },
  nav:             { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn:          { padding: 6 },
  monthLabel:      { fontSize: 15, fontWeight: '700', color: '#111827' },
  row:             { flexDirection: 'row', justifyContent: 'center' },
  dayHeader:       { width: 36, textAlign: 'center', fontSize: 11, fontWeight: '600', color: '#9ca3af', paddingVertical: 4, margin: 1 },
  cell:            { width: 36, height: 36, justifyContent: 'center', alignItems: 'center', borderRadius: 8, margin: 1 },
  cellSelected:    { backgroundColor: '#2c3e50' },
  cellHighlight:   { backgroundColor: '#dbeafe' },
  dayText:         { fontSize: 13, color: '#374151' },
  dayTextSelected: { color: '#fff', fontWeight: '700' },
  dayTextHighlight:{ color: '#1d4ed8', fontWeight: '600' },
});

const styles = StyleSheet.create({
  centered:        { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 },
  headerTitle:     { fontSize: 22, fontWeight: '700', color: '#111827' },
  headerSub:       { fontSize: 13, color: '#9ca3af', marginTop: 2 },
  viewToggle:      { flexDirection: 'row', backgroundColor: '#f3f4f6', borderRadius: 10, padding: 2, gap: 2 },
  viewBtn:         { padding: 8, borderRadius: 8 },
  viewBtnActive:   { backgroundColor: '#2c3e50' },
  filterRow:       { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 12, paddingBottom: 8, gap: 6 },
  filterBtn:       { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: '#f3f4f6', borderWidth: 1, borderColor: '#e5e7eb' },
  filterBtnActive: { backgroundColor: '#2c3e50', borderColor: '#2c3e50' },
  filterText:      { fontSize: 12, fontWeight: '600', color: '#6b7280' },
  filterTextActive:{ color: '#fff' },
  pilotDropdown:   { flexDirection: 'row', alignItems: 'center', gap: 6, marginHorizontal: 12, marginBottom: 8, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e5e7eb', alignSelf: 'flex-start' },
  pilotDropdownText:{ fontSize: 13, color: '#374151', fontWeight: '500' },
  weekNav:         { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#f3f4f6' },
  weekNavBtn:      { padding: 8 },
  weekNavCenter:   { flex: 1, alignItems: 'center' },
  weekNavLabel:    { fontSize: 14, fontWeight: '700', color: '#111827' },
  weekNavHint:     { fontSize: 10, color: '#9ca3af', marginTop: 2 },
  exportBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#fff', borderRadius: 10, paddingVertical: 10, marginBottom: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  exportBtnText:   { fontSize: 13, fontWeight: '600', color: '#2c3e50' },
  actionRow:       { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingBottom: 8 },
  actionBtn:       { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#fff', borderRadius: 10, paddingVertical: 10, borderWidth: 1, borderColor: '#e5e7eb' },
  actionBtnText:   { fontSize: 13, fontWeight: '600', color: '#2c3e50' },
  noDateBanner:    { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#fffbeb', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10, marginBottom: 8, borderWidth: 1, borderColor: '#fde68a' },
  noDateBannerText:{ flex: 1, fontSize: 13, color: '#d97706', fontWeight: '500' },
  card:            { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: '#f3f4f6' },
  cardTop:         { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  typeIcon:        { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  cardTitle:       { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 1 },
  cardId:          { fontSize: 11, color: '#9ca3af', marginBottom: 2 },
  cardSub:         { fontSize: 12, color: '#6b7280' },
  statusBadge:     { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20, flexShrink: 0 },
  statusText:      { fontSize: 11, fontWeight: '600' },
  cardFooter:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#f3f4f6', paddingTop: 10 },
  pilotRow:        { flexDirection: 'row', alignItems: 'center', gap: 6 },
  pilotDot:        { width: 8, height: 8, borderRadius: 4 },
  pilotName:       { fontSize: 13, color: '#374151', fontWeight: '500' },
  aircraftName:    { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  noPilot:         { fontSize: 13, color: '#9ca3af', fontStyle: 'italic' },
  assignBtn:       { flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 6, paddingHorizontal: 10, backgroundColor: '#f3f4f6', borderRadius: 10 },
  assignBtnText:   { fontSize: 12, fontWeight: '600', color: '#2c3e50' },
  datesRow:        { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#f9fafb' },
  dateItem:        { alignItems: 'flex-start' },
  dateLabelText:   { fontSize: 10, color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 },
  dateValue:       { fontSize: 13, color: '#374151', fontWeight: '500' },
  dateProposed:    { alignItems: 'flex-start' },
  dateProposedValue:    { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dateProposedText:     { fontSize: 13, color: '#1d4ed8', fontWeight: '700' },
  dateProposedEmpty:    { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#eff6ff', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: '#bfdbfe' },
  dateProposedEmptyText:{ fontSize: 12, color: '#1d4ed8', fontWeight: '600' },
  legend:          { flexDirection: 'row', flexWrap: 'wrap', gap: 12, backgroundColor: '#fff', borderRadius: 12, padding: 12, marginBottom: 12, borderWidth: 1, borderColor: '#f3f4f6' },
  legendItem:      { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendText:      { fontSize: 12, color: '#374151', fontWeight: '500' },
  dateSeparator:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginVertical: 12 },
  dateLine:        { flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  dateLabel:       { fontSize: 12, fontWeight: '700', color: '#6b7280', textTransform: 'capitalize' },
  empty:           { alignItems: 'center', paddingVertical: 60 },
  emptyTitle:      { fontSize: 16, fontWeight: '600', color: '#374151', marginTop: 12 },
  emptyText:       { fontSize: 13, color: '#9ca3af', textAlign: 'center', marginTop: 4 },
  modalOverlay:    { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  modalBox:        { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24 },
  modalHeader:     { paddingHorizontal: 20, paddingVertical: 18, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  modalTitle:      { fontSize: 16, fontWeight: '700', color: '#111827' },
  modalSub:        { fontSize: 13, color: '#9ca3af', marginTop: 2 },
  calHint:         { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 20, paddingTop: 12, paddingBottom: 4 },
  calHintText:     { fontSize: 12, color: '#9ca3af' },
  calSelected:     { paddingHorizontal: 20, paddingVertical: 10 },
  calSelectedText: { fontSize: 13, color: '#6b7280' },
  calBtns:         { flexDirection: 'row', gap: 8, paddingHorizontal: 20, paddingBottom: 8 },
  calBtnPrimary:   { flex: 2, backgroundColor: '#2c3e50', borderRadius: 14, paddingVertical: 14, alignItems: 'center' },
  calBtnPrimaryText:   { fontSize: 15, fontWeight: '700', color: '#fff' },
  calBtnSecondary:     { flex: 1, backgroundColor: '#f3f4f6', borderRadius: 14, paddingVertical: 14, alignItems: 'center' },
  calBtnSecondaryText: { fontSize: 14, fontWeight: '600', color: '#6b7280' },
  pilotOption:       { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f9fafb' },
  pilotOptionFirst:  { borderTopWidth: 1, borderTopColor: '#f3f4f6', marginTop: 4 },
  pilotOptionActive: { backgroundColor: '#f0fdf4' },
  pilotOptionIcon:   { width: 32, height: 32, borderRadius: 16, backgroundColor: '#f3f4f6', justifyContent: 'center', alignItems: 'center' },
  pilotOptionDot:    { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  pilotOptionText:   { fontSize: 15, color: '#111827' },
  pilotOptionCurrent:{ fontSize: 11, color: '#16a34a', fontWeight: '600', backgroundColor: '#dcfce7', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 20 },
  modalCancel:       { marginHorizontal: 20, marginTop: 8, paddingVertical: 14, alignItems: 'center', backgroundColor: '#f3f4f6', borderRadius: 14, marginBottom: 8 },
  modalCancelText:   { fontSize: 15, fontWeight: '600', color: '#6b7280' },
  aircraftPicker:         { marginLeft: 44, marginBottom: 6, backgroundColor: '#f8faff', borderRadius: 10, padding: 8, borderWidth: 1, borderColor: '#dbeafe' },
  aircraftOption:         { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 6, borderRadius: 8 },
  aircraftOptionActive:   { backgroundColor: '#eff6ff' },
  aircraftOptionText:     { flex: 1, fontSize: 13, color: '#374151' },
  noAircraftText:         { fontSize: 12, color: '#9ca3af', fontStyle: 'italic', paddingVertical: 6, paddingHorizontal: 4 },
  confirmAssignBtn:       { marginTop: 8, backgroundColor: '#1d4ed8', borderRadius: 8, paddingVertical: 10, alignItems: 'center' },
  confirmAssignBtnGray:   { backgroundColor: '#6b7280' },
  confirmAssignBtnText:   { color: '#fff', fontWeight: '700', fontSize: 13 },
});
